import { Surface, Text, VStack } from '@/components/Atoms'
import usePermissions from '@/hooks/permissions/usePermissions'
import {
  inboxSearchSchema,
  useInbox,
  useMarkInboxTaskRead,
  type InboxTask,
} from '@/hooks/queries/credit/useInbox'
import { inboxModeAtom } from '@/stores/creditInbox'
import { getInboxTaskMode, type InboxMode } from '@repo/constants'
import { Button } from '@repo/ui/components'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useAtom } from 'jotai'
import { SearchXIcon } from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { isTaskNamed, taskKey } from './-data/inboxTask'
import { InboxDetail } from './-components/InboxDetail'
import { InboxList } from './-components/InboxList'

export const Route = createFileRoute('/_auth/_modules/credit/inbox/')({
  validateSearch: inboxSearchSchema,
  component: RouteComponent,
})

/**
 * The reviewer/approver inbox (FUZE-616): one queue of everything the signed-in user owes a
 * decision on, replacing the per-type tables.
 *
 * Filters and the open task live in the URL rather than in component state, so a reload keeps the
 * reviewer's place and a link to a specific request can be pasted into Slack. The server owns the
 * feed — what is in it, its order, its tab counts and each row's read state — because all of those
 * depend on DOA assignments and service levels the client cannot see.
 */
function RouteComponent() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { canUser } = usePermissions()
  const [storedMode, setStoredMode] = useAtom(inboxModeAtom)

  /**
   * CLRs and blocked orders never share the list. The URL's mode wins; a deep link to a task opens
   * the queue that task lives in; otherwise the reader returns to the queue they last worked. A
   * reader holding only one of the two permissions is pinned to it and never sees the toggle.
   */
  const canSeeClrs = canUser('credit_limit_reviews_view')
  const canSeeBlockedOrders = canUser('credit_blocked_orders_view')
  const preferredMode =
    search.mode ?? (search.taskType ? getInboxTaskMode(search.taskType) : storedMode)
  const mode: InboxMode = !canSeeBlockedOrders
    ? 'clr'
    : !canSeeClrs
      ? 'blocked_orders'
      : preferredMode
  const isBlockedMode = mode === 'blocked_orders'

  // However the mode was reached — the toggle, a cockpit link, a pasted URL — it is the one the
  // reader is now working, so it is the one the sidebar link should reopen.
  useEffect(() => {
    if (mode !== storedMode) setStoredMode(mode)
  }, [mode, storedMode, setStoredMode])

  // Each mode sends only the filters it has, so a CLR filter left in the URL cannot empty the
  // blocked-orders list (or the reverse).
  const { data, isLoading, refetch } = useInbox(
    isBlockedMode
      ? { mode, bucket: search.bucket, scope: search.scope, keyword: search.keyword }
      : {
          mode,
          stage: search.stage,
          type: search.type,
          kind: search.kind,
          scope: search.scope,
          sort: search.sort,
          keyword: search.keyword,
        },
  )
  const markRead = useMarkInboxTaskRead()

  const tasks = data?.tasks ?? []
  const { taskType, taskId } = search
  const selected =
    taskType !== undefined && taskId !== undefined
      ? (tasks.find((task) => isTaskNamed(task, taskType, taskId)) ?? null)
      : null

  /**
   * A link from outside the inbox — the blocked-orders cockpit's row link, or a URL someone pasted
   * — names a task that may not be in THIS reader's feed. It can be pending at a band they do not
   * hold, or resolved, which takes it out of the feed entirely.
   *
   * The auto-select below would answer that by opening an unrelated request, which reads as the
   * link having worked and is the one wrong thing to do with a decision queue. So an explicit task
   * in the URL suppresses the auto-select, and the pane says what happened instead.
   */
  const isDeepLinked = search.taskType !== undefined && search.taskId !== undefined
  const isDeepLinkMissing = isDeepLinked && !selected && !isLoading

  /**
   * The row to open once the current one is decided. Resolved requests leave the feed, so without
   * this the selection falls to null, the empty state renders for a frame, and the auto-select below
   * puts a different row back — which reads as the whole pane flashing. Deciding should behave like
   * any queue: it advances.
   */
  const selectedIndex = selected ? tasks.indexOf(selected) : -1
  const nextTask =
    tasks.slice(selectedIndex + 1).find((task) => !task.isResolved) ??
    tasks.find((task) => !task.isResolved && task !== selected) ??
    null

  // Open the first outstanding request when nothing is selected, so the queue is workable on
  // arrival. `replace` so it does not add a history entry the back button has to walk through.
  const firstUnresolved = tasks.find((task) => !task.isResolved)
  useEffect(() => {
    if (selected || isDeepLinked || !firstUnresolved) return
    void navigate({
      search: (prev) => ({ ...prev, taskType: firstUnresolved.type, taskId: firstUnresolved.id }),
      replace: true,
    })
  }, [selected, isDeepLinked, firstUnresolved, navigate])

  const selectTask = (task: InboxTask) => {
    void navigate({ search: (prev) => ({ ...prev, taskType: task.type, taskId: task.id }) })
    // Fire-and-forget: the row's weight is cosmetic, and the mutation invalidates the feed itself.
    if (!task.isRead) markRead.mutate({ type: task.type, id: task.id })
  }

  /**
   * Switching queues drops every filter that belongs to the other one, and the selection with them.
   * Scope and keyword carry over: "my direct work" and "this payer" mean the same in both.
   */
  const setMode = (next: InboxMode) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        mode: next,
        stage: undefined,
        bucket: undefined,
        type: undefined,
        kind: undefined,
        sort: undefined,
        taskType: undefined,
        taskId: undefined,
      }),
    })
  }

  const setSearch = (patch: Partial<typeof search>) => {
    // Filters change what is in the list, so a selection made under the old filter may no longer be
    // there. Clearing it lets the auto-select above pick the first row of the new result.
    void navigate({
      search: (prev) => ({ ...prev, ...patch, taskType: undefined, taskId: undefined }),
    })
  }

  return (
    // 109px = MainHeader (h-16) + SecondaryHeader (h-11) + the app header's bottom border.
    <div className="flex h-[calc(100svh-109px)] min-h-0">
      <Surface
        tone="level-1"
        border={false}
        className="w-[420px] shrink-0 border-r"
        as="aside"
        aria-label="Inbox task list"
      >
        <InboxList
          tasks={tasks}
          counts={data?.counts}
          isLoading={isLoading}
          blockedOrdersStatus={data?.blockedOrdersStatus}
          onRetry={() => void refetch()}
          selectedKey={selected ? taskKey(selected) : null}
          search={search}
          mode={mode}
          hasModeToggle={canSeeClrs && canSeeBlockedOrders}
          onModeChange={setMode}
          onSelect={selectTask}
          onSearchChange={setSearch}
        />
      </Surface>
      <main className="min-w-0 flex-1">
        {isDeepLinkMissing ? (
          <TaskNotInInbox
            onClear={() =>
              void navigate({
                search: (prev) => ({
                  ...prev,
                  type: undefined,
                  taskType: undefined,
                  taskId: undefined,
                }),
                replace: true,
              })
            }
          />
        ) : (
          <InboxDetail
            task={selected}
            onDecided={(remainingReviewId) =>
              void navigate({
                search: (prev) =>
                  remainingReviewId !== undefined
                    ? { ...prev, taskId: remainingReviewId }
                    : { ...prev, taskType: nextTask?.type, taskId: nextTask?.id },
                replace: true,
              })
            }
          />
        )}
      </main>
    </div>
  )
}

/**
 * What a deep link lands on when the request it names is not this reader's to see.
 *
 * Deliberately not an error: the commonest cause is ordinary — the record is pending at a band
 * someone else holds, or it has already been decided — and the reader did nothing wrong. It names
 * both possibilities rather than guessing, because the feed cannot tell them apart: a task that is
 * not in it is simply absent, with no reason attached.
 */
function TaskNotInInbox(props: { onClear: () => void }) {
  const { t } = useTranslation('credit')

  return (
    <VStack
      gap="sm"
      align="center"
      className="text-muted-foreground h-full justify-center px-6 text-center"
    >
      <SearchXIcon className="size-8" />
      <Text tone="muted" className="max-w-sm text-pretty">
        {t('clr.inbox.detail.notInYourInbox')}
      </Text>
      <Button type="button" variant="secondary" size="sm" onClick={props.onClear}>
        {t('clr.inbox.detail.showMyInbox')}
      </Button>
    </VStack>
  )
}
